import React, { useState, useEffect, useCallback } from "react";
import { apiGet } from "../services/api";

import MasterTabs from "../components/admin/master/MasterTabs";
import AdminFinance from "../components/admin/finance/AdminFinance";
import OtherExpenses from "../components/admin/finance/OtherExpenses";
import ReferenceStats from "../components/admin/ReferenceStats";
import BackupRestore from "../components/admin/backup/BackupRestore";
import UserManagement from "../components/admin/UserManagement";
import AuditLogViewer from "../components/admin/AuditLogViewer";

import "./ModernAdmin.css"; // UPDATED IMPORT

/** Response from GET /health */
interface HealthResponse {
  status?: string;
  procedure_master_count?: number;
  db_connected?: boolean;
  chart_history_exists?: boolean;
  procedure_sessions_columns_ok?: boolean;
  services?: { migrations?: number; postgres?: boolean };
  [key: string]: unknown;
}

interface DashboardStats {
  total_visits: number;
  total_collected: number;
  total_bills: number;
  inventory_items: number;
  new_patients?: number;
  gender_split?: { male?: number; female?: number; other?: number; unknown?: number };
  appointments_by_hour?: number[];
  revenue_date?: string;
  revenue_clinical?: number;
  revenue_pharmacy?: number;
  revenue_ortho?: number;
  revenue_total?: number;
}

interface TrendPoint {
  date: string;
  visits: number;
  revenue: number;
}

export default function AdminHome() {
  const [tab, setTab] = useState("dashboard");
  const [financeSubTab, setFinanceSubTab] = useState("reports");
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [revenueDate, setRevenueDate] = useState(new Date().toISOString().slice(0, 10));
  const [trendLoading, setTrendLoading] = useState(false);
  const [weeklyTrend, setWeeklyTrend] = useState<TrendPoint[]>([]);

  const [showReferenceChart, setShowReferenceChart] = useState(false);

  const genderSplit = stats?.gender_split || { male: 0, female: 0, other: 0, unknown: 0 };
  const genderTotal =
    (genderSplit.male || 0) +
    (genderSplit.female || 0) +
    (genderSplit.other || 0) +
    (genderSplit.unknown || 0);
  const hourly = stats?.appointments_by_hour || [];
  const hourlyMax = Math.max(1, ...hourly.map((v) => Number(v || 0)));

  const normalizeStats = (d: unknown): DashboardStats => ({
    total_visits: (d as { total_visits?: number })?.total_visits ?? 0,
    total_collected: (d as { total_collected?: number })?.total_collected ?? 0,
    total_bills: (d as { total_bills?: number })?.total_bills ?? 0,
    inventory_items: (d as { inventory_items?: number })?.inventory_items ?? 0,
    new_patients: (d as { new_patients?: number })?.new_patients ?? 0,
    gender_split: (d as { gender_split?: DashboardStats["gender_split"] })?.gender_split ?? { male: 0, female: 0, other: 0, unknown: 0 },
    appointments_by_hour: (d as { appointments_by_hour?: number[] })?.appointments_by_hour ?? [],
    revenue_date: (d as { revenue_date?: string })?.revenue_date,
    revenue_clinical: (d as { revenue_clinical?: number })?.revenue_clinical ?? 0,
    revenue_pharmacy: (d as { revenue_pharmacy?: number })?.revenue_pharmacy ?? 0,
    revenue_ortho: (d as { revenue_ortho?: number })?.revenue_ortho ?? 0,
    revenue_total: (d as { revenue_total?: number })?.revenue_total ?? 0,
  });

  const loadStats = useCallback(() => {
    setLoadingStats(true);
    setError(null);
    const dateParam = revenueDate ? `?date=${encodeURIComponent(revenueDate)}` : "";
    apiGet(`/api/admin/dashboard/summary${dateParam}`)
      .then((data) => setStats(normalizeStats(data)))
      .catch((err: unknown) => {
        const e = err as Error & { status?: number };
        if (e.status === 502 || e.status === 503 || e.status === 504) {
          setError("Backend unavailable. Run: docker compose logs backend");
        } else {
          setError("Failed to load dashboard statistics");
        }
      })
      .finally(() => setLoadingStats(false));
  }, [revenueDate]);

  const loadWeeklyTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const base = revenueDate ? new Date(revenueDate) : new Date();
      if (Number.isNaN(base.getTime())) {
        setWeeklyTrend([]);
        return;
      }
      const dates: string[] = [];
      for (let i = 6; i >= 0; i -= 1) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        dates.push(d.toISOString().slice(0, 10));
      }
      const points = await Promise.all(
        dates.map(async (d) => {
          try {
            const res = (await apiGet(`/api/admin/dashboard/summary?date=${encodeURIComponent(d)}`)) as DashboardStats;
            return {
              date: d,
              visits: Number(res?.total_visits || 0),
              revenue: Number(res?.revenue_total || 0),
            } as TrendPoint;
          } catch {
            return { date: d, visits: 0, revenue: 0 } as TrendPoint;
          }
        })
      );
      setWeeklyTrend(points);
    } finally {
      setTrendLoading(false);
    }
  }, [revenueDate]);

  useEffect(() => {
    const savedTab = localStorage.getItem("admin:tab");
    const savedFinance = localStorage.getItem("admin:financeSubTab");
    if (savedTab) setTab(savedTab);
    if (savedFinance) setFinanceSubTab(savedFinance);
  }, []);

  useEffect(() => {
    if (tab === "dashboard") loadStats();
  }, [tab, loadStats]);

  useEffect(() => {
    if (tab === "dashboard") void loadWeeklyTrend();
  }, [tab, loadWeeklyTrend]);

  useEffect(() => {
    apiGet<HealthResponse>("/health")
      .then((h) => setHealth(h))
      .catch(() => { });
  }, []);

  useEffect(() => {
    localStorage.setItem("admin:tab", tab);
  }, [tab]);

  useEffect(() => {
    localStorage.setItem("admin:financeSubTab", financeSubTab);
  }, [financeSubTab]);

  return (
    <div className="admin-container">
      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="adm-brand">
          <h1>
            🛡️ Admin <span className="badge">PRO</span>
          </h1>
        </div>

        <nav className="adm-nav">
          <button className={`adm-nav-btn ${tab === "dashboard" ? "active" : ""}`} onClick={() => setTab("dashboard")}>
            <span>📊</span> Dashboard
          </button>
          <button className={`adm-nav-btn ${tab === "master" ? "active" : ""}`} onClick={() => setTab("master")}>
            <span>🗂️</span> Master Data
          </button>
          <button className={`adm-nav-btn ${tab === "revenue" ? "active" : ""}`} onClick={() => setTab("revenue")}>
            <span>📈</span> Financials
          </button>
          <button className={`adm-nav-btn ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")}>
            <span>👥</span> Staff Users
          </button>
          <button className={`adm-nav-btn ${tab === "backup" ? "active" : ""}`} onClick={() => setTab("backup")}>
            <span>💾</span> Backups
          </button>
          <button className={`adm-nav-btn ${tab === "audit" ? "active" : ""}`} onClick={() => setTab("audit")}>
            <span>🧾</span> Audit
          </button>
        </nav>

        {/* Module Jump Nav */}
        <div className="adm-jump">
          <div className="adm-jump-title">Jump to Module</div>
          {[
            { label: '🏥 Reception', path: '/reception' },
            { label: '🦷 CareFlow', path: '/careflow' },
            { label: '📋 Manager', path: '/manager' },
            { label: '👨‍⚕️ Doctor', path: '/doctor' },
            { label: '💊 Pharmacy', path: '/pharmacy' },
            { label: '📅 Treatments', path: '/treatments' },
            { label: '🔔 Followups', path: '/followups' },
          ].map(({ label, path }) => (
            <button
              key={path}
              type="button"
              onClick={() => window.open(path, '_blank')}
              className="adm-jump-btn"
            >
              {label} ↗
            </button>
          ))}
        </div>
      </aside>

      {/* CONTENT AREA */}
      <div className="admin-content">

        {/* HEADER */}
        <header className="admin-header">
          <div className="page-title">
            <h2 className="ui-page-title adm-page-title">{tab.charAt(0).toUpperCase() + tab.slice(1)}</h2>
          </div>
          <div className="system-status-pill">
            <span className={`status-dot ${health?.db_connected ? "" : "error"}`}></span>
            System {health?.db_connected ? "Operational" : "Degraded"}
          </div>
        </header>

        {/* WORKSPACE */}
        <main className="admin-workspace ui-page">
          {tab === "dashboard" && (
            <div className="dashboard-view fade-in-up">
              {error && <div className="error-banner">⚠️ {error}</div>}

              <div className="adm-revenue-toolbar">
                <div className="toolbar-title">Daily Revenue Date</div>
                <input
                  type="date"
                  value={revenueDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRevenueDate(e.target.value)}
                />
                <button className="btn-glass" type="button" onClick={loadStats} disabled={loadingStats}>
                  {loadingStats ? "Loading…" : "Refresh"}
                </button>
                <span className="toolbar-note">
                  Showing daily revenue for {stats?.revenue_date || revenueDate}
                </span>
              </div>

              {/* STATS GRID */}
              <div className="adm-dashboard-grid ui-grid">
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">🏥</div>
                  <div className="stat-val">{loadingStats ? "…" : stats?.total_visits || 0}</div>
                  <div className="stat-label">Daily Visits</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">👥</div>
                  <div className="stat-val">{loadingStats ? "…" : stats?.new_patients || 0}</div>
                  <div className="stat-label">New Patients</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">💰</div>
                  <div className="stat-val">₹{(stats?.total_collected || 0).toLocaleString("en-IN")}</div>
                  <div className="stat-label">Clinical Revenue</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">🧾</div>
                  <div className="stat-val">{loadingStats ? "…" : stats?.total_bills || 0}</div>
                  <div className="stat-label">Daily Bills</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">📦</div>
                  <div className="stat-val">{loadingStats ? "…" : stats?.inventory_items || 0}</div>
                  <div className="stat-label">Inventory Updates</div>
                </div>

                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">💊</div>
                  <div className="stat-val">₹{(stats?.revenue_pharmacy || 0).toLocaleString("en-IN")}</div>
                  <div className="stat-label">Pharmacy Revenue</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">🦷</div>
                  <div className="stat-val">₹{(stats?.revenue_ortho || 0).toLocaleString("en-IN")}</div>
                  <div className="stat-label">Ortho Revenue</div>
                </div>
                <div className="adm-stat-card ui-card">
                  <div className="stat-icon-wrap">🧮</div>
                  <div className="stat-val">₹{(stats?.revenue_total || 0).toLocaleString("en-IN")}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
              </div>

              <div className="adm-panel-row">
                <div className="adm-panel ui-card">
                  <h3>👥 Patient Mix</h3>
                  <div className="adm-mix-list">
                    {[
                      { label: "Male", value: genderSplit.male || 0, color: "#2563eb" },
                      { label: "Female", value: genderSplit.female || 0, color: "#ec4899" },
                      { label: "Other", value: genderSplit.other || 0, color: "#22c55e" },
                      { label: "Unknown", value: genderSplit.unknown || 0, color: "#94a3b8" },
                    ].map((g) => {
                      const pct = genderTotal ? Math.round((g.value / genderTotal) * 100) : 0;
                      return (
                        <div key={g.label} className="adm-mix-row">
                          <div className="adm-mix-label">{g.label}</div>
                          <div className="adm-mix-bar">
                            <div className="adm-mix-fill" style={{ width: `${pct}%`, background: g.color }} />
                          </div>
                          <div className="adm-mix-value">{g.value}</div>
                        </div>
                      );
                    })}
                    {genderTotal === 0 && <div className="adm-mix-empty">No patient data for this date.</div>}
                  </div>
                </div>

                <div className="adm-panel ui-card">
                  <h3>⏰ Appointments by Hour</h3>
                  <div className="adm-hourly-grid">
                    {Array.from({ length: 24 }).map((_, i) => {
                      const count = Number(hourly[i] || 0);
                      const height = Math.max(6, Math.round((count / hourlyMax) * 100));
                      return (
                        <div key={i} className="adm-hourly-col">
                          <div className="adm-hourly-bar">
                            <div
                              className="adm-hourly-fill"
                              style={{ height: `${height}%`, background: count ? "#38bdf8" : "#cbd5f5" }}
                            />
                          </div>
                          <span className="adm-hourly-label">{i}:00</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="adm-panel-row">
                {/* MAINTENANCE PANEL */}
                <div className="adm-panel ui-card">
                  <h3>⚡ Quick Actions</h3>
                  <div className="action-buttons">
                    <button className="btn-gold" onClick={loadStats} disabled={loadingStats}>
                      🔄 Refresh Data
                    </button>
                    <button className="btn-glass" onClick={() => setShowReferenceChart(v => !v)}>
                      📊 {showReferenceChart ? "Hide" : "Show"} Growth Chart
                    </button>
                    <button className="btn-glass" onClick={() => window.open("/billing/clinical", "_blank")}>
                      💳 Clinical Billing ↗
                    </button>
                  </div>
                  {showReferenceChart && (
                    <div className="adm-panel-spacer">
                      <ReferenceStats />
                    </div>
                  )}
                </div>

                {/* HEALTH WIDGET */}
                <div className="adm-panel">
                  <h3>🩺 System Health</h3>
                  {health ? (
                    <div className="health-grid">
                      <div className="health-item">
                        <span className="health-label">Database</span>
                        <span className={`health-val ${health.db_connected ? "ok" : "error"}`}>
                          {health.db_connected ? "Connected" : "Error"}
                        </span>
                      </div>
                      <div className="health-item">
                        <span className="health-label">API</span>
                        <span className="health-val ok">Online</span> {/* Assuming if we render this, API served it */}
                      </div>
                      <div className="health-item">
                        <span className="health-label">Migrations</span>
                        <span className="health-val">{health.services?.migrations ?? "—"}</span>
                      </div>
                      <div className="health-item">
                        <span className="health-label">History</span>
                        <span className={`health-val ${health.chart_history_exists ? "ok" : "error"}`}>
                          {health.chart_history_exists ? "Active" : "Missing"}
                        </span>
                      </div>
                    </div>
                  ) : <div className="text-secondary">Checking system status...</div>}
                </div>
              </div>

              <div className="adm-trend-panel">
                <div className="adm-trend-head">
                  <h3>7-Day Trend</h3>
                  <button className="btn-glass" type="button" onClick={() => void loadWeeklyTrend()} disabled={trendLoading}>
                    {trendLoading ? "Loading…" : "Refresh Trend"}
                  </button>
                </div>
                {weeklyTrend.length === 0 ? (
                  <div className="adm-trend-empty">No trend data available.</div>
                ) : (
                  <div className="adm-trend-grid">
                    {weeklyTrend.map((point) => (
                      <div key={point.date} className="adm-trend-card">
                        <div className="adm-trend-date">{new Date(point.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                        <div className="adm-trend-visits">Visits: <strong>{point.visits}</strong></div>
                        <div className="adm-trend-revenue">Revenue: <strong>₹{point.revenue.toLocaleString("en-IN")}</strong></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "master" && (
            <div className="fade-in-up">
              <MasterTabs />
            </div>
          )}

          {tab === "backup" && <BackupRestore />}
          {tab === "users" && <UserManagement />}
          {tab === "audit" && (
            <div className="fade-in-up">
              <AuditLogViewer />
            </div>
          )}

          {tab === "revenue" && (
            <div className="finance-wrapper fade-in-up">
              <div className="finance-sub-tabs">
                <button
                  type="button"
                  className={financeSubTab === "reports" ? "active" : ""}
                  onClick={() => setFinanceSubTab("reports")}
                >
                  📊 Finance Reports
                </button>
                <button
                  type="button"
                  className={financeSubTab === "expenses" ? "active" : ""}
                  onClick={() => setFinanceSubTab("expenses")}
                >
                  💸 Other Expenses
                </button>
              </div>
              {financeSubTab === "reports" && <AdminFinance />}
              {financeSubTab === "expenses" && <OtherExpenses />}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
